import {
  MarketingShell,
  Seo,
  Container,
  Section,
  PageHero,
  SectionIntro,
  FeatureGrid,
  PricingTable,
  Accordion,
  CTASection,
  Reveal,
} from './kit.jsx'

const PLANS = [
  {
    name: 'Starter',
    price: '$49',
    period: '/seat/mo',
    tagline: 'For solo operators and small crews working a defined territory.',
    features: [
      'Full lead database for your chosen regions',
      'Every vertical in the database',
      'Basic multi-step email sequences',
      'Send from your own Gmail account',
      'Sortable, searchable lead tables',
      'CSV export of any filtered list',
    ],
    cta: { label: 'Start free', to: '/signup' },
  },
  {
    name: 'Growth',
    price: '$99',
    period: '/seat/mo',
    featured: true,
    badge: 'Most popular',
    tagline: 'For growing sales teams that want the whole motion in one place.',
    features: [
      'All 50 U.S. regions unlocked',
      'Unlimited outreach sequences',
      'Deal flow pipeline, new to closed-won',
      'Activity & data insights dashboards',
      'In-app assistant for live answers',
      'Team collaboration & region assignment',
      'Everything in Starter',
    ],
    cta: { label: 'Start free', to: '/signup' },
  },
  {
    name: 'Scale',
    price: 'Custom',
    period: '',
    tagline: 'For agencies and multi-location businesses running many teams.',
    features: [
      'Everything in Growth',
      'Multiple isolated workspaces',
      'Priority support with an SLA',
      'Guided onboarding & data setup',
      'API access for your own tooling',
      'Volume pricing across seats',
    ],
    cta: { label: 'Talk to sales', to: '/contact' },
  },
]

const INCLUDED = [
  {
    icon: 'shield',
    title: 'Secure by default',
    body: 'Cognito authentication, encrypted storage, and Gmail sending scoped to your own account — your data stays yours.',
  },
  {
    icon: 'boxes',
    title: '10,942 verified leads',
    body: 'The full verified database of 10,942 companies across the verticals you sell into, organized by industry and region.',
  },
  {
    icon: 'sparkle',
    title: 'Dark & light themes',
    body: 'A workspace built for long days — switch between dark and light modes across the entire app, any time.',
  },
  {
    icon: 'refresh',
    title: 'Weekly data updates',
    body: 'Company records, contacts, and service areas are refreshed weekly so your reps never work a stale list.',
  },
]

const FAQ = [
  {
    q: 'How does billing work?',
    a: 'Starter and Growth are billed per seat, per month. Pick monthly or annual — annual saves two months. You can change plans or add seats at any time, and charges are prorated automatically. Scale is quoted based on seats, workspaces, and support level.',
  },
  {
    q: 'What counts as a seat?',
    a: 'A seat is one rep or team member with their own login, region assignments, and connected Gmail. Add or remove seats whenever your team changes — you only pay for the seats you have active in a given month.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. There are no long-term contracts on Starter or Growth. Cancel from your workspace settings and you keep access through the end of the current billing period. Export your data on the way out — no lock-in.',
  },
  {
    q: 'Do I own my data?',
    a: 'Your outreach, pipeline, notes, and activity belong to you. Export any filtered list to CSV whenever you like, and Scale customers can pull everything through the API. We never sell or share your workspace data.',
  },
  {
    q: 'Is the lead data included in the price?',
    a: 'Yes. All 10,942 verified companies are included in every plan — there are no per-lead fees or list purchases. Starter covers the regions you choose; Growth and Scale unlock all every region.',
  },
]

export default function PricingPage() {
  return (
    <MarketingShell active="pricing">
      <Seo
        title="Pricing"
        description="Simple per-seat pricing for Revailo Sales. Starter, Growth, and Scale plans — all include 10,942 verified B2B leads, outreach, and pipeline."
        path="/pricing"
        keywords="Revailo Sales pricing, b2b leads pricing, lead data cost, per seat pricing, sales software pricing"
      />

      <Section>
        <Reveal>
          <PageHero
            eyebrow="Pricing"
            title={
              <>
                Simple per-seat pricing. <br className="hidden sm:inline" />
                The whole database, every plan.
              </>
            }
            lead="Pay for the reps you have, not the leads you use. Every plan ships with all 10,942 verified companies — no per-lead fees, no list purchases, no data project."
            primary={{ label: 'Start free', to: '/signup' }}
            secondary={{ label: 'Talk to sales', to: '/contact' }}
          />
        </Reveal>
      </Section>

      <Section sm>
        <Container>
          <Reveal>
            <PricingTable plans={PLANS} />
          </Reveal>
        </Container>
      </Section>

      <Section id="included">
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="In every plan"
              title="What every seat gets, no matter the tier"
              lead="Starter, Growth, and Scale all start from the same foundation — the full dataset, secure auth, and the workspace your team actually works in."
            />
          </Reveal>
          <div className="mt-14">
            <Reveal>
              <FeatureGrid items={INCLUDED} cols={4} />
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section sm id="faq">
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="Pricing FAQ"
              title="Questions before you pick a plan"
              lead="Billing, seats, cancellation, and data ownership — the things teams ask us most."
            />
          </Reveal>
          <div className="mt-10" style={{ maxWidth: 760, margin: '40px auto 0' }}>
            <Reveal>
              <Accordion items={FAQ} />
            </Reveal>
          </div>
        </Container>
      </Section>

      <CTASection
        title="Start free, upgrade when your pipeline does."
        body="Spin up a workspace, load your regions, and send your first sequence today. Move to Growth or Scale whenever your team grows."
        primary={{ label: 'Start free', to: '/signup' }}
        secondary={{ label: 'Book a demo', to: '/demo' }}
      />
    </MarketingShell>
  )
}
